import { type ReactNode } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Parsed = { title: string; message: string; detail: string };

function parseError(err: unknown): Parsed {
  if (isRouteErrorResponse(err)) {
    return {
      title: err.status === 404 ? 'Página no encontrada' : 'Algo ha fallado',
      message:
        err.status === 404
          ? 'La página que buscas no existe o ha sido movida.'
          : 'Se ha producido un error inesperado.',
      detail: `${err.status} ${err.statusText}`,
    };
  }
  const detail = err instanceof Error ? err.message : String(err ?? '');
  return {
    title: 'Algo ha fallado',
    message:
      'Se ha producido un error inesperado. Prueba a recargar la página o vuelve al inicio.',
    detail,
  };
}

function Details({ text }: { text: string }): ReactNode {
  if (!text || !import.meta.env.DEV) return null;
  return (
    <pre className="mt-md max-h-40 overflow-auto rounded-lg bg-surface-elevated p-sm text-left text-[11px] text-text-tertiary">
      {text}
    </pre>
  );
}

function Actions(): ReactNode {
  return (
    <div className="flex flex-col gap-sm sm:flex-row sm:justify-center">
      <Button onClick={() => window.location.reload()} variant="secondary">
        Recargar
      </Button>
      <Button onClick={() => (window.location.href = '/')}>
        Volver al inicio
      </Button>
    </div>
  );
}

export function ErrorPage(): ReactNode {
  const err = useRouteError();
  const { title, message, detail } = parseError(err);
  return (
    <div className="flex min-h-dvh items-center justify-center bg-void p-base">
      <div className="w-full max-w-[28rem] space-y-lg text-center">
        <div className="flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-red/10">
            <AlertTriangle size={24} className="text-brand-red" />
          </div>
        </div>
        <div className="space-y-sm">
          <h1 className="text-xl font-bold text-text-primary">{title}</h1>
          <p className="text-sm text-text-tertiary">{message}</p>
        </div>
        <Actions />
        <Details text={detail} />
      </div>
    </div>
  );
}
